<!--
  DiagAttemptsPanel.vue
  콜봇 월 정산 · 연결 진단 탭의 2-6 "연결 진단" — 칩 셋과 번호별 시도 결과 표. 읽기 전용.

  요청: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 2-6", 시도 데이터 원천은
        ~/md/back/상담사-연결시도-이벤트-pbx-ami요청.md(call.attempt.ended), 확정 계약은
        ~/md/front/콜봇-연결시도-attempts-call-api구현회신.md(admin 1~3·5)
  - 칩: 시도 기록 수집률 = flow.handoffs_with_attempts ÷ flow.handoffs_attempts_expected(둘 다 null 이면 '수집 전',
    분모 0 이면 '—' — flow.handoffs 로 나누지 않는다) · 벨 없이 끝난 시도 n건(번호 행 + 번호 밖 시도의 no_ring) ·
    미종료 통화 n건(summary.not_final_calls). 건수 칩은 0건이면 초록.
  - 번호별 시도 결과 표: 번호(끝 4자리) | 시도 | 응답 | 30초 무응답 | 받을 수 없는 상태 | 벨 중 고객 종료 |
    벨 없이 끝남 | 다른 연결로 대체. per_number[].attempts 가 null 이면 시도 칸을
    "수집 전(수집 시작 YYYY-MM-DD)"(수집 시작일을 모르면 "수집 전")으로 보인다. 어느 번호와도 맞지 않는 시도
    (per_number_unattributed.attempts — 지워진 번호 등)가 있으면 "등록 번호 밖" 행을 더한다 — 합계는 그 달 회사 시도
    전체다. `other`(실패 등)는 열 없이 시도 수에만 든다.
  - 문구 규칙: "받을 수 없는 상태(통화 중·전원 꺼짐·수신 거절 등)". 원인을 특정 사업자 탓으로 쓰지 않는다.
    순차 연결이라 뒤 순번 번호에 "벨 중 고객 종료"가 몰린다는 각주를 단다.
  값 계산은 settlementRules.diagAttemptsView 가 한다(수집 시작일은 'YYYY-MM-DD' 문자열).
-->
<template>
  <section class="attempts-card" :aria-labelledby="titleId">
    <header class="attempts-card__head">
      <h2 :id="titleId" class="attempts-card__title">연결 진단</h2>
      <ul class="attempts-chips" aria-label="연결 진단 요약">
        <li v-for="chip in chips" :key="chip.key">
          <a-tooltip :title="chip.tip">
            <span class="attempts-chip" :class="`is-${chip.tone}`" tabindex="0">
              {{ chip.label }} <b>{{ chip.value }}</b>
            </span>
          </a-tooltip>
        </li>
      </ul>
    </header>

    <p v-if="!view.collected" class="attempts-card__pending" role="note">
      번호마다 몇 번 울렸고 왜 못 받았는지는 pbx-ami 시도 이벤트 배포 뒤부터 쌓입니다. 이 달은 {{ view.pendingLabel }}입니다.
    </p>

    <div class="attempts-table-wrap">
      <table class="attempts-table">
        <thead>
          <tr>
            <th scope="col">번호</th>
            <th v-for="col in ATTEMPT_COLUMNS" :key="col.key" scope="col" class="is-num">
              <a-tooltip :title="col.tip">
                <span class="attempts-table__th">{{ col.label }}</span>
              </a-tooltip>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!view.rows.length && !view.unattributed">
            <td :colspan="ATTEMPT_COLUMNS.length + 1" class="attempts-table__empty">등록된 상담사 번호가 없습니다.</td>
          </tr>
          <tr v-for="row in view.rows" :key="row.key">
            <th scope="row" class="attempts-table__number">
              {{ row.number }}
              <small v-if="row.name">{{ row.name }}</small>
            </th>
            <td v-if="!row.attempts" :colspan="ATTEMPT_COLUMNS.length" class="attempts-table__pending">
              {{ view.pendingLabel }}
            </td>
            <template v-else>
              <td
                v-for="col in ATTEMPT_COLUMNS"
                :key="col.key"
                class="is-num"
                :class="{ 'is-zero': row.attempts[col.key] === 0 }"
              >
                {{ formatNumber(row.attempts[col.key]) }}
              </td>
            </template>
          </tr>
          <tr v-if="view.unattributed">
            <th scope="row" class="attempts-table__number" :title="UNATTRIBUTED_TIP">
              등록 번호 밖
              <small>지워진 번호 등</small>
            </th>
            <td
              v-for="col in ATTEMPT_COLUMNS"
              :key="col.key"
              class="is-num"
              :class="{ 'is-zero': view.unattributed[col.key] === 0 }"
            >
              {{ formatNumber(view.unattributed[col.key]) }}
            </td>
          </tr>
        </tbody>
        <tfoot v-if="view.totals">
          <tr>
            <th scope="row">합계</th>
            <td v-for="col in ATTEMPT_COLUMNS" :key="col.key" class="is-num">{{ formatNumber(view.totals[col.key]) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <ul class="attempts-notes">
      <li>받을 수 없는 상태 = {{ UNAVAILABLE_TEXT }}.</li>
      <li>{{ SEQUENTIAL_NOTE }}</li>
      <li v-if="view.totals && view.totals.other > 0">
        기타 결과 {{ formatNumber(view.totals.other) }}건(연결 실패 등)은 열 없이 시도 수에만 들어 있습니다.
      </li>
    </ul>
  </section>
</template>

<script setup>
import { computed, getCurrentInstance } from 'vue';

import { EMPTY, formatNumber, isNumber } from '../../service/settlementFormat';
import { diagAttemptsView } from '../../service/settlementRules';

const props = defineProps({
  /** 월간 이용 리포트 응답(GET .../callbot/stats/monthly) */
  stats: { type: Object, required: true },
});

const UNAVAILABLE_TEXT = '받을 수 없는 상태(통화 중·전원 꺼짐·수신 거절 등)';
const SEQUENTIAL_NOTE = '순차 연결이라 뒤 순번 번호에 "벨 중 고객 종료"가 몰립니다.';
const UNATTRIBUTED_TIP = '시도한 번호가 지금 등록된 상담사 번호 어느 것과도 맞지 않는 시도(지워진 번호 등)';

/** 시도 열 — per_number[].attempts 의 키(settlementRules.ATTEMPT_COLUMN_KEYS 순서) */
const ATTEMPT_COLUMNS = [
  { key: 'total', label: '시도', tip: '그 번호에 연결을 시도한 횟수(순차 연결의 한 칸이 1회). 옆 칸에 없는 기타 결과도 센다' },
  { key: 'answered', label: '응답', tip: '직원이 받은 시도' },
  { key: 'no_answer_30s', label: '30초 무응답', tip: '벨이 30초 동안 울렸지만 받지 않은 시도' },
  { key: 'unavailable', label: '받을 수 없는 상태', tip: UNAVAILABLE_TEXT },
  { key: 'caller_hangup_ringing', label: '벨 중 고객 종료', tip: '벨이 울리는 중에 고객이 끊은 시도' },
  { key: 'no_ring', label: '벨 없이 끝남', tip: '연결을 지시했지만 벨이 울리지 않고 끝난 시도' },
  { key: 'superseded', label: '다른 연결로 대체', tip: '울리는 중 당겨받기 등 새 연결 지시로 바뀐 시도' },
];

const titleId = `st-diag-attempts-${getCurrentInstance()?.uid ?? 0}`;

const num = (v) => (isNumber(v) ? v : null);

const view = computed(() => diagAttemptsView(props.stats));

/** 수집률 칩 — 상태마다 값·색·툴팁 */
function rateChip(rate, pendingTip) {
  if (rate.state === 'pending') return { value: rate.value, tone: 'muted', tip: pendingTip };
  if (rate.state === 'none') {
    return {
      value: rate.value,
      tone: 'default',
      tip: '이 달 시도 기록이 남아야 할 직원 연결 요청이 없습니다(vox 직접 전환·연결할 번호가 없던 요청은 세지 않습니다).',
    };
  }
  if (rate.state === 'partial') {
    return { value: rate.value, tone: 'default', tip: '수집률의 분자·분모 중 하나가 리포트 응답에 없습니다.' };
  }
  return {
    value: rate.value,
    tone: rate.complete ? 'ok' : 'default',
    tip:
      `시도 기록이 남아야 할 직원 연결 요청 ${formatNumber(rate.expected)}통 중 기록이 있는 요청 ` +
      `${formatNumber(rate.withAttempts)}통. vox 직접 전환과 연결할 번호가 없던 요청은 원래 시도가 없어 세지 않습니다. ` +
      '시도 기록은 best-effort 라 빠질 수 있습니다.',
  };
}

const chips = computed(() => {
  const v = view.value;
  const notFinal = num(props.stats?.summary?.not_final_calls);

  const pendingTip = `시도 기록이 아직 없습니다 — ${v.pendingLabel}. pbx-ami 시도 이벤트 배포 뒤부터 쌓입니다.`;

  let noRing = { value: v.collected ? EMPTY : '수집 전', tone: 'muted', tip: pendingTip };
  if (v.noRing !== null) {
    noRing = {
      value: `${formatNumber(v.noRing)}건`,
      tone: v.noRing === 0 ? 'ok' : 'warn',
      tip: '연결을 지시했지만 벨이 울리지 않고 끝난 시도 — 등록 번호 밖 시도까지 더한 수',
    };
  }
  return [
    { key: 'rate', label: '시도 기록 수집률', ...rateChip(v.rate, pendingTip) },
    { key: 'noRing', label: '벨 없이 끝난 시도', ...noRing },
    {
      key: 'notFinal',
      label: '미종료 통화',
      value: notFinal === null ? EMPTY : `${formatNumber(notFinal)}건`,
      tone: notFinal === null ? 'muted' : notFinal === 0 ? 'ok' : 'warn',
      tip: '아직 끝나지 않은 통화 — 정산 확정 전 확인이 필요합니다(NOT_FINAL_CALLS).',
    },
  ];
});
</script>

<style scoped lang="scss">
@use '../../styles/settlement' as *;

.attempts-card {
  @include st-card;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
  }

  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: $st-text;
  }

  &__pending {
    margin: 0;
    padding: 8px 12px;
    border-radius: 8px;
    background: $st-bg;
    font-size: 12.5px;
    color: $st-text-secondary;
  }
}

.attempts-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0 0 0 auto;
  padding: 0;
  list-style: none;
}

.attempts-chip {
  @include st-pill($st-text-secondary, #fff, $st-border-strong);
  padding: 1px 10px;
  font-size: 12px;
  font-weight: 500;
  cursor: help;

  b {
    @include st-num;
    font-weight: 700;
  }

  &.is-ok {
    @include st-pill-tone($st-ok, $st-ok-bg, $st-ok-border);
  }

  &.is-warn {
    @include st-pill-tone($st-warning, $st-warning-bg, $st-warning-border);
  }

  &.is-muted {
    @include st-pill-tone($st-muted, $st-bg, $st-border);
  }

  &:focus-visible {
    outline: 2px solid $st-accent;
    outline-offset: 1px;
  }
}

.attempts-table-wrap {
  overflow-x: auto;
  border: 1px solid $st-border;
  border-radius: 10px;
}

.attempts-table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  font-size: 12.5px;

  th,
  td {
    padding: 8px 12px;
    // !important — CertiAccManage.vue 의 전역 td { border-bottom: transparent !important } 가 td 줄을 지워 항목 칸(th)에만 줄이 남는다
    border-bottom: 1px solid $st-border !important;
    text-align: left;
    white-space: nowrap;
  }

  thead th {
    background: $st-bg;
    font-size: 12px;
    font-weight: 600;
    color: $st-text-secondary;
  }

  .is-num {
    @include st-num;
    text-align: right;
  }

  td.is-zero {
    color: $st-muted;
  }

  tfoot th,
  tfoot td {
    border-bottom: 0 !important;
    background: $st-bg;
    font-weight: 700;
    color: $st-text;
  }

  &__th {
    border-bottom: 1px dotted $st-muted;
    cursor: help;
  }

  &__number {
    @include st-num;
    font-weight: 600;
    color: $st-text;

    small {
      margin-left: 6px;
      font-size: 11.5px;
      font-weight: 400;
      color: $st-muted;
    }
  }

  &__pending {
    background: repeating-linear-gradient(135deg, #fafafa 0 8px, #f5f5f5 8px 16px);
    color: $st-muted;
    text-align: center !important;
  }

  &__empty {
    color: $st-muted;
    text-align: center !important;
  }
}

.attempts-notes {
  margin: 0;
  padding-left: 18px;
  font-size: 12px;
  color: $st-muted;
}
</style>
